/**
 * Dateien SAMMELN, bevor sie hochgeladen werden (HJ-028).
 *
 * Vorher stand hier ein nacktes `<input type="file" multiple>`. Technisch
 * nahm es mehrere Dateien — aber nur in EINEM Zug mit Cmd/Strg-Klick. Wer
 * erst den Transportauftrag wählte und dann die Skizze, hatte danach nur noch
 * die Skizze: Eine zweite Auswahl ersetzt beim Browser die erste. Im Testlauf
 * am 24.10. sah das aus wie „es geht nur eine Datei".
 *
 * Jetzt wächst eine Liste: Jede Auswahl und jedes Hineinziehen hängt an,
 * jede Zeile hat „Entfernen" und ihre eigene Art. Das Feld selbst wird nach
 * jeder Auswahl wieder geleert — sonst stünde dort ein Dateiname, der mit der
 * Liste darunter nichts zu tun hat (und nach dem Eintragen der Name der
 * vorigen Tour, HJ-022).
 *
 * ═══ WAS GAR NICHT ERST IN DIE LISTE KOMMT ═══
 *
 * Nur Bilder und PDF, höchstens 20 MB — dieselben Grenzen wie der
 * Belegspeicher. Eine Datei, die der Speicher ohnehin ablehnt, erst zu
 * sammeln und nach dem Eintragen als Fehler zu melden, hieße: Die Tour steht,
 * die Datei fehlt, und gemerkt wird es erst danach. Abgelehntes steht deshalb
 * sofort da, mit Grund — still verschwinden darf nichts.
 *
 * ═══ ZIEHEN UND ABLEGEN IST EINE ABKÜRZUNG, KEIN EIGENER WEG ═══
 *
 * Die Ablagefläche umschließt das normale Dateifeld. Wer keine Maus hat oder
 * nicht ziehen kann, wählt per Tastatur wie bisher; beide Wege landen in
 * derselben Funktion.
 */
import { useId, useRef, useState, type DragEvent } from 'react';

export type GewaehlteDatei = {
  /** Nur für React und „Entfernen" — eine Datei hat keine eigene Kennung. */
  schluessel: string;
  datei: File;
  art: string;
};

/** Dieselbe Grenze wie der Belegspeicher; steht auch im Hinweis am Feld. */
const HOECHSTENS_BYTES = 20 * 1024 * 1024;

/**
 * Manche Wege (Ziehen aus dem Mailprogramm) liefern eine Datei OHNE Typ.
 * Dann entscheidet die Endung — und die Datei bekommt den Typ mit, damit der
 * Belegspeicher sie nicht als „unbekannt" ablehnt. Ohne das stünde eine
 * echte PDF als „nur Bilder und PDF" in der Ablehnung.
 */
const TYP_NACH_ENDUNG: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  webp: 'image/webp',
};

function mitTyp(datei: File): File {
  if (datei.type) return datei;
  const endung = datei.name.split('.').pop()?.toLowerCase() ?? '';
  const typ = TYP_NACH_ENDUNG[endung];
  return typ
    ? new File([datei], datei.name, { type: typ, lastModified: datei.lastModified })
    : datei;
}

function erlaubt(datei: File): string | null {
  const typOk = datei.type.startsWith('image/') || datei.type === 'application/pdf';
  if (!typOk) return 'nur Bilder und PDF';
  if (datei.size > HOECHSTENS_BYTES) return 'größer als 20 MB';
  return null;
}

/**
 * Dieselbe Datei zweimal gewählt ist EINE Datei. Verglichen werden Name,
 * Größe und Typ — bewusst NICHT die Änderungszeit: Manche Wege (Ziehen aus
 * dem Mailprogramm, Dateien aus der Vorschau) liefern bei jedem Mal eine
 * neue, und dann stünde der Transportauftrag doppelt an der Tour. Zwei
 * verschiedene Dateien mit gleichem Namen UND auf das Byte gleicher Größe
 * gibt es im Büroalltag nicht.
 */
function gleich(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.type === b.type;
}

function groesse(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

export function Dateiablage(eigenschaften: {
  dateien: GewaehlteDatei[];
  setzen: (dateien: GewaehlteDatei[]) => void;
  arten: { wert: string; text: string }[];
  /** Die Art, die eine neu hinzugefügte Datei bekommt. */
  vorgabeArt: string;
  disabled?: boolean;
}) {
  const hinweisId = useId();
  const feldId = useId();
  const feld = useRef<HTMLInputElement>(null);
  const [abgelehnt, setAbgelehnt] = useState<string[]>([]);
  const [schonDabei, setSchonDabei] = useState<string[]>([]);
  const [darueber, setDarueber] = useState(false);

  function hinzufuegen(neu: File[]) {
    const angenommen: GewaehlteDatei[] = [];
    const nein: string[] = [];
    const doppelt: string[] = [];
    for (const roh of neu) {
      const datei = mitTyp(roh);
      const grund = erlaubt(datei);
      if (grund) {
        nein.push(`${datei.name} (${grund})`);
        continue;
      }
      const schonDa =
        eigenschaften.dateien.some((g) => gleich(g.datei, datei)) ||
        angenommen.some((g) => gleich(g.datei, datei));
      if (schonDa) {
        // Nicht still verwerfen — sonst sucht jemand die zweite Datei, die
        // er gerade gewählt zu haben glaubt.
        doppelt.push(datei.name);
        continue;
      }
      angenommen.push({
        schluessel: crypto.randomUUID(),
        datei,
        art: eigenschaften.vorgabeArt,
      });
    }
    setAbgelehnt(nein);
    setSchonDabei(doppelt);
    if (angenommen.length > 0) {
      eigenschaften.setzen([...eigenschaften.dateien, ...angenommen]);
    }
  }

  function ablegen(ereignis: DragEvent<HTMLDivElement>) {
    ereignis.preventDefault();
    setDarueber(false);
    if (eigenschaften.disabled) return;
    hinzufuegen([...(ereignis.dataTransfer?.files ?? [])]);
  }

  return (
    <div className="feld" data-dateien>
      {/* Ein echtes `label`: Ohne es sagte ein Screenreader am Dateifeld nur
          „Datei auswählen, Schalter" — wofür, stand nirgends. */}
      <label className="feld__marke" htmlFor={feldId}>
        Dateien
      </label>
      <div
        data-ablage
        className={darueber ? 'ablage ablage--darueber' : 'ablage'}
        // `preventDefault` beim Darüberziehen ist die Voraussetzung dafür,
        // dass der Browser das Ablegen überhaupt zulässt — sonst öffnet er
        // die PDF stattdessen im Tab, und das Formular ist weg.
        onDragOver={(e) => {
          e.preventDefault();
          if (!eigenschaften.disabled) setDarueber(true);
        }}
        onDragLeave={() => setDarueber(false)}
        onDrop={ablegen}
      >
        <input
          ref={feld}
          id={feldId}
          type="file"
          multiple
          accept="image/*,application/pdf"
          disabled={eigenschaften.disabled}
          aria-describedby={hinweisId}
          onChange={(e) => {
            hinzufuegen([...(e.target.files ?? [])]);
            // Leeren, damit (a) das Feld nicht einen Namen zeigt, der mit der
            // Liste nichts zu tun hat, und (b) dieselbe Datei nach „Entfernen"
            // wieder gewählt werden kann — ohne Leeren meldet der Browser
            // dann keine Änderung.
            e.target.value = '';
          }}
        />
        <span className="meta">oder Dateien hierher ziehen</span>
      </div>
      <span className="feld__hinweis" id={hinweisId}>
        Transportauftrag des Kunden, Skizzen, alles zur Fahrt. Bilder und PDF,
        höchstens 20 MB je Datei. Jede Auswahl kommt zur Liste dazu.
      </span>

      {abgelehnt.length > 0 && (
        <span className="feld__fehler" role="alert">
          Nicht angenommen: {abgelehnt.join(', ')}.
        </span>
      )}

      {schonDabei.length > 0 && (
        <span className="feld__hinweis" role="status">
          Schon in der Liste: {schonDabei.join(', ')}.
        </span>
      )}

      {eigenschaften.dateien.length > 0 && (
        <ul className="dateiliste">
          {eigenschaften.dateien.map((g) => (
            <li key={g.schluessel} data-datei className="dateiliste__zeile">
              <span className="dateiliste__name">
                {g.datei.name}
                <span className="meta"> · {groesse(g.datei.size)}</span>
              </span>
              <select
                aria-label={`Art von ${g.datei.name}`}
                value={g.art}
                disabled={eigenschaften.disabled}
                onChange={(e) =>
                  eigenschaften.setzen(
                    eigenschaften.dateien.map((d) =>
                      d.schluessel === g.schluessel ? { ...d, art: e.target.value } : d,
                    ),
                  )
                }
              >
                {eigenschaften.arten.map((a) => (
                  <option key={a.wert} value={a.wert}>
                    {a.text}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="knopf knopf--still knopf--klein"
                disabled={eigenschaften.disabled}
                aria-label={`${g.datei.name} entfernen`}
                onClick={() => {
                  eigenschaften.setzen(
                    eigenschaften.dateien.filter((d) => d.schluessel !== g.schluessel),
                  );
                  // Der Knopf verschwindet mit seiner Zeile; ohne das landete
                  // der Fokus im Nichts, und mit der Tastatur ginge es von
                  // ganz oben weiter.
                  feld.current?.focus();
                }}
              >
                Entfernen
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
